import './GroupAdmins.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GroupAdminsProps } from './GroupAdmins.interface';
import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { editGroupActions, editGroupSelector } from '$store/editGroup';
import { Loader } from '$shared/components';
import { IcAdd28 } from '$assets';
import { showModal } from '$navigation/helper';
import { GroupAdminsItem } from '$core/GroupAdmins/GroupAdminsItem/GroupAdminsItem';
import { useTranslator } from '$hooks';

export const GroupAdmins: FC<GroupAdminsProps> = ({ groupId }) => {
  const dispatch = useDispatch();
  const { isAdminsLoading, isAdminsFailed, admins } = useSelector(editGroupSelector);
  const t = useTranslator();

  useEffect(() => {
    dispatch(editGroupActions.loadAdmins(groupId));
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(editGroupActions.loadAdmins(groupId));
  }, [dispatch, groupId]);

  const handleAdd = useCallback(() => {
    showModal(`followers-${groupId}`, false, {
      promote: 1,
    });
  }, [dispatch, groupId]);

  function renderContent() {
    if (isAdminsLoading) {
      return <Loader isWrapped />;
    }

    if (isAdminsFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleRetry}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!admins.length) {
      return (
        <Placeholder
          actions={
            <Button size="small" onClick={handleAdd}>
              {t('group_admins_add')}
            </Button>
          }
        >
          {t('group_admins_empty')}
        </Placeholder>
      );
    }

    return admins.map((item) => (
      <GroupAdminsItem key={item.userId} userId={item.userId} groupId={groupId} />
    ));
  }

  return (
    <>
      <Header
        showCloseButton
        rightIcons={[
          {
            icon: <IcAdd28 />,
            onClick: handleAdd,
          },
        ]}
      >
        {t('group_admins_title')}
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
